import { EventRegistry } from '@core/services/gtm/gtm.events2';

/**
 * Recursively flattens a nested object of event keys into a flat mapping of event key to event name.
 * Allows to nest event keys by category/location and then have event keys grouped by event name (for generic events) or left as is (for milestone events).
 * e.g. { BACKGROUND_MODAL_EVENTS: { CROPPER_EVENTS: { dyb_cropper_rotate: 'dyb_crop_interaction' }, scenario_start: 'dyb_scenario_start' } } -> { dyb_cropper_rotate: 'dyb_crop_interaction', dyb_cropper_crop_left: 'dyb_crop_interaction', scenario_start: 'dyb_scenario_start' }
 */
type Flatten<
	GroupTree, // The nested event keys
	GroupOverride extends Record<string, keyof EventRegistry>, // Override to map event groups to correct event name (generic events)
	ParentGroup extends string | undefined = undefined, // Remember the parent group key during recursion
> = GroupTree extends object // primative guard (important for the recursion call)
	? {
			[Prop in keyof GroupTree]: GroupTree[Prop] extends string // iterate over each property in the event category or event group in recursion // If the value is a string, emit a { leaf : eventName } record
				? {
						[Leaf in GroupTree[Prop] & string]: ParentGroup extends keyof GroupOverride ? GroupOverride[ParentGroup] : GroupTree[Prop]; // If the event group is mapped to a generic event, use that event name, else it is a milestone event so use the leaf value as the event name
					} // If not a string, recurse into nested object, remembering its group key
				: Flatten<GroupTree[Prop], GroupOverride, Prop & string>;
		}[keyof GroupTree] // turn the object of unions into a union of records
	: never;

/**
 * Turns a union of records into an intersection of records
 * Flatten returns a union of records representing event keys mapped to event names however if this union is used to index the `EventRegistry` we cannot type check for indivial event's (interface's) parameters because it has collapsed to only the common properties of all the interfaces in the union.
 * To fix this we convert the union of records into an intersection of records so that when we index `EventRegistry` with it, we get a type that is the intersection of all the interfaces in the union, which preserves all the individual properties of each interface.
 * e.g. `{ a: A } | { b: B }` -> `{ a: A } & { b: B }`
 */
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void ? I : never;

/**
 * Build the final Record<eventKey, eventName> type:
 *   1.  Flatten -> flat mapping of event key to event name (collapses to union)
 *   2.  UnionToIntersection -> converts flat map union to intersection, preserving individual event interface properties
 */
type BuildMapping<Groups, Overrides extends Record<string, keyof EventRegistry>> = UnionToIntersection<Flatten<Groups, Overrides>>;

/**
 * Builds a run-time and compile-time mapping of event keys to event names.
 * @param groups The event groups to build the mapping from.
 * @param overrides Optional overrides for specific event groups (to map an event group to a different event name, e.g. for generic events).
 * @returns A record mapping event keys to event names.
 */
export function buildKeyToEvent<
	const Groups extends Record<string, unknown>,
	const Overrides extends Partial<Record<string, keyof EventRegistry>> = Record<string, never>,
>(groups: Groups, overrides: Overrides = {} as Overrides) {
	const out: Record<string, keyof EventRegistry> = {};

	const walk = (obj: any, currentGroup?: string) => {
		for (const [k, v] of Object.entries(obj)) {
			if (typeof v === 'string') {
				const group = currentGroup ?? k;
				out[v] = (overrides as any)[group] ?? v;
			} else if (v && typeof v === 'object') {
				walk(v, k);
			}
		}
	};

	walk(groups);
	return out as BuildMapping<Groups, Overrides>;
}

export type LeafValues<T> = T extends string ? T : T extends Record<string, infer V> ? LeafValues<V> : never;

export function collectLeafStrings<T>(obj: T, out: LeafValues<T>[] = []): LeafValues<T>[] {
	if (typeof obj === 'string') {
		out.push(obj as LeafValues<T>);
	} else if (obj && typeof obj === 'object') {
		for (const v of Object.values(obj as Record<string, unknown>)) {
			collectLeafStrings(v as any, out);
		}
	}
	return out;
}
